import type { User } from "@crm/access/authorization/types";
import type { AuthContext } from "@crm/access/types/router";
import { useMemo } from "react";
import { authClient } from "./client";
import { ClientAuthContext } from "./context";
import {
  accountPendingAuthContext,
  anonymousAuthContext,
  authenticatedAuthContext,
  failedAuthContext,
  pendingAuthContext,
} from "./state";

export { loadClientPermissions } from "./state";

const USER_ROLES = new Set<User["role"]>([
  "admin",
  "teacher",
  "reviewer",
  "coach",
  "coach-manager",
  "student",
  "sales",
]);
const USER_STATUSES = new Set<User["status"]>(["pending", "active", "disabled"]);

const isRecord = (value: unknown): value is Record<PropertyKey, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nullableString = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

export function userFromSessionIdentity(identity: unknown): User | null {
  if (!isRecord(identity)) return null;
  const { id, email, name, role, status } = identity;
  if (
    typeof id !== "string" ||
    typeof email !== "string" ||
    typeof name !== "string" ||
    !USER_ROLES.has(role as User["role"]) ||
    !USER_STATUSES.has(status as User["status"])
  ) {
    return null;
  }

  const userRole = role as User["role"];
  const userStatus = status as User["status"];

  return {
    id,
    email,
    name,
    image: nullableString(identity.image),
    emailVerified: identity.emailVerified ? String(identity.emailVerified) : null,
    role: userRole,
    staffId: nullableString(identity.staffId),
    studentId: nullableString(identity.studentId),
    activeSiteId: nullableString(identity.activeSiteId),
    aiBaseUrl: nullableString(identity.aiBaseUrl),
    aiModel: nullableString(identity.aiModel),
    sioExternalLinkBlacklist: null,
    sioExternalLinkPreferredDomains: null,
    aiCapabilityModels: null,
    status: userStatus,
    lastLoginAt: identity.lastLoginAt instanceof Date ? identity.lastLoginAt : null,
    createdAt: identity.createdAt instanceof Date ? identity.createdAt : null,
    updatedAt: identity.updatedAt instanceof Date ? identity.updatedAt : null,
  };
}

export function authContextFromSessionIdentity(identity: unknown): AuthContext {
  const user = userFromSessionIdentity(identity);
  if (!user) return failedAuthContext("Session user shape is invalid");

  switch (user.status) {
    case "active":
      return authenticatedAuthContext(user);
    case "pending":
      return accountPendingAuthContext(user);
    case "disabled":
      return failedAuthContext("Session user is disabled");
  }
}

export function resolveClientAuthContext(input: {
  readonly sessionIdentity: unknown;
  readonly isPending: boolean;
  readonly serverAuth?: AuthContext;
}): AuthContext {
  if (input.isPending) {
    if (
      input.serverAuth?.status === "authenticated" ||
      input.serverAuth?.status === "account_pending" ||
      input.serverAuth?.status === "anonymous"
    ) {
      return input.serverAuth;
    }
    return pendingAuthContext();
  }

  if (!input.sessionIdentity) return anonymousAuthContext();
  return authContextFromSessionIdentity(input.sessionIdentity);
}

/** session → discriminated AuthContext with SSR bootstrap hydration. */
export function useSessionAuthContext(serverAuth?: AuthContext): AuthContext {
  const { data: session, isPending } = authClient.useSession();
  return useMemo((): AuthContext => {
    if (typeof window === "undefined" && serverAuth) return serverAuth;
    return resolveClientAuthContext({
      sessionIdentity: session?.user,
      isPending,
      serverAuth,
    });
  }, [session?.user, isPending, serverAuth]);
}

export function ClientAuthProvider({
  serverAuth,
  children,
}: {
  serverAuth?: AuthContext;
  children: React.ReactNode;
}) {
  const clientAuth = useSessionAuthContext(serverAuth);
  return <ClientAuthContext.Provider value={clientAuth}>{children}</ClientAuthContext.Provider>;
}
